import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { personalInfo } from "../../data/personalInfo";

const STAMPS = [
  "/stamps/side_image_03bdf8f713.png",
  "/stamps/side_image_08618b84d5.png",
  "/stamps/side_image_2100a24971.png",
  "/stamps/side_image_3f7d975dbe.png",
  "/stamps/side_image_597d682aa8.png",
  "/stamps/side_image_5cf34ebeba.png",
  "/stamps/side_image_5f6d8b83f5.png",
  "/stamps/side_image_634b7fc709.png",
  "/stamps/side_image_65e2222cf0.png",
  "/stamps/side_image_679d86de6e.png",
  "/stamps/side_image_687a0c4882.png",
  "/stamps/side_image_9e3fef7b5c.png",
  "/stamps/side_image_9fa7482ed2.png",
  "/stamps/side_image_a25a57687c.png",
  "/stamps/side_image_b8712d50b8.png",
  "/stamps/side_image_c2834573ae.png",
  "/stamps/side_image_e33be583aa.png",
  "/stamps/side_image_f257da599d.png",
  "/stamps/side_image_fd7fe295e8.png",
];

const N = 4;
const FLIP_MS = 460;
const FLIP_DURATION = 0.4;
const SERIF = { fontFamily: "'Georgia', 'Times New Roman', serif" };

const SLOTS = [
  { left: "6%", top: "9%", width: "38%", rotate: -7 },
  { right: "5%", top: "19%", width: "35%", rotate: 6 },
  { left: "14%", bottom: "7%", width: "42%", rotate: 4 },
  { right: "4%", bottom: "15%", width: "34%", rotate: -6 },
];

const StampPage = ({ page }) => {
  const count = page === 4 ? 3 : 4;
  const stamps = STAMPS.slice(page * 4, page * 4 + count);

  return (
    <div className="absolute inset-0 overflow-hidden page-paper select-none">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span
          className="text-4xl tracking-[0.5em] uppercase text-[#000]/[0.05] -rotate-3"
          style={SERIF}
        >
          Nepal
        </span>
      </div>
      <div className="absolute inset-2 border border-[#000]/10 pointer-events-none" />
      <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[8px] font-mono tracking-[0.35em] text-[#000]/30">
        NEPAL · PASSPORT
      </div>

      {stamps.map((src, s) => {
        const slot = SLOTS[(s + page) % 4];
        return (
          <img
            key={src}
            src={src}
            alt="stamp"
            draggable={false}
            className="absolute pointer-events-none select-none mix-blend-multiply"
            style={{
              ...(slot.left ? { left: slot.left } : { right: slot.right }),
              ...(slot.top ? { top: slot.top } : { bottom: slot.bottom }),
              width: slot.width,
              transform: `rotate(${slot.rotate}deg)`,
              opacity: 0.94,
              filter:
                "sepia(0.12) saturate(0.9) contrast(1.05) drop-shadow(0 2px 2px rgba(0,0,0,0.15))",
            }}
          />
        );
      })}

      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[8px] font-mono tracking-widest text-[#000]/35">
        № 0000{page + 1}
      </div>
    </div>
  );
};

const FirstPage = () => (
  <div className="absolute inset-0 overflow-hidden page-paper select-none">
    <img
      src="/stamps/np.png"
      alt="passport first page"
      className="absolute inset-0 w-full h-full object-cover"
      draggable={false}
    />
    <div className="absolute bottom-4 left-0 right-0 flex justify-center text-[8px] font-mono tracking-[0.3em] text-white/85 drop-shadow animate-pulse">
      CLICK TO OPEN ▸
    </div>
  </div>
);

const HolderPage = () => (
  <div className="absolute inset-0 overflow-hidden page-paper select-none flex flex-col justify-center px-6">
    <div className="text-[8px] font-mono tracking-[0.4em] text-[#000]/35 text-center mb-10">
      NEPAL · PASSPORT
    </div>
    <div className="text-lg tracking-[0.25em] text-[#111]" style={SERIF}>
      {personalInfo.name.toUpperCase()}
    </div>
    <div className="text-[8px] font-mono tracking-widest text-[#000]/45 mt-2">
      NATIONALITY — NEPALESE
    </div>
    <div className="text-[8px] font-mono tracking-widest text-[#000]/45 mt-2">
      PASSPORT № — PD-2026·NP-0411
    </div>
    <div className="text-[8px] font-mono tracking-widest text-[#000]/45 mt-2">
      PLACE OF ISSUE — KATHMANDU
    </div>
  </div>
);

const EndPage = () => (
  <div className="absolute inset-0 overflow-hidden page-paper flex flex-col items-center justify-center select-none">
    <div className="text-[8px] font-mono tracking-[0.4em] text-[#000]/35 mb-5">
      END OF TRAVEL LOG
    </div>
    <div className="relative">
      <div className="w-32 h-32 rounded-full border-[3px] border-[#c8102e] opacity-70 flex items-center justify-center rotate-[-8deg]">
        <div className="w-24 h-24 rounded-full border-2 border-[#c8102e] flex flex-col items-center justify-center">
          <span className="text-[11px] font-bold text-[#c8102e] tracking-widest" style={SERIF}>
            RE-ENTRY
          </span>
          <span className="text-[11px] font-bold text-[#c8102e] tracking-widest" style={SERIF}>
            APPROVED
          </span>
        </div>
      </div>
      <div className="absolute -top-1 right-1 text-[9px] font-bold text-[#c8102e] rotate-12" style={SERIF}>
        JAN 2026
      </div>
    </div>
    <div className="mt-6 text-[10px] text-[#000]/40 italic" style={SERIF}>
      thanks for travelling with us.
    </div>
  </div>
);

const LEAVES = [
  { front: <FirstPage />, back: <HolderPage /> },
  { front: <StampPage page={0} />, back: <StampPage page={1} /> },
  { front: <StampPage page={2} />, back: <StampPage page={3} /> },
  { front: <StampPage page={4} />, back: <EndPage /> },
];

export const Passport = ({ open, onClose }) => {
  const [flipCount, setFlipCount] = useState(0);
  const [flipping, setFlipping] = useState(null);
  const flipRef = useRef(0);
  const busy = useRef(false);

  const turn = (dir) => {
    if (busy.current) return;
    const fc = flipRef.current;
    const next = fc + dir;
    if (next < 0 || next > N) return;

    busy.current = true;
    setFlipping({ leaf: dir === 1 ? fc : fc - 1, to: dir === 1 ? -180 : 0 });
    setTimeout(() => {
      flipRef.current = next;
      setFlipCount(next);
      setFlipping(null);
      busy.current = false;
    }, FLIP_MS);
  };

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") turn(1);
      if (e.key === "ArrowLeft") turn(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (busy.current) return;
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-[#0a0a0a]/85 backdrop-blur-sm" onClick={close} />

          <motion.div
            className="relative w-[min(94vw,720px)]"
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-white/70">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nothing-red opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-nothing-red" />
                </span>
                PASSPORT_VIEWER
              </div>
              <button
                onClick={close}
                aria-label="Close passport"
                className="p-2 rounded-full border border-white/15 bg-white/5 text-white/80 hover:text-white hover:border-nothing-red hover:bg-nothing-red/20 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="relative w-full aspect-[720/505] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.6)] select-none [perspective:2200px] rounded-lg">
              <div className="absolute inset-0 page-paper rounded-lg overflow-hidden" />

              <div
                className="absolute inset-y-0 right-0 left-0 rounded-lg overflow-hidden [transform-style:preserve-3d]"
              >
                {LEAVES.map((leaf, i) => {
                  const isAnimating = flipping && flipping.leaf === i;
                  const target = isAnimating ? flipping.to : i < flipCount ? -180 : 0;
                  const z = isAnimating ? N + 10 : i < flipCount ? i + 1 : N - i + 1;
                  return (
                    <motion.div
                      key={i}
                      className="absolute top-0 bottom-0 left-1/2 w-1/2"
                      style={{
                        transformOrigin: "left center",
                        transformStyle: "preserve-3d",
                        zIndex: z,
                        willChange: "transform",
                      }}
                      initial={false}
                      animate={{ rotateY: target }}
                      transition={
                        isAnimating
                          ? { duration: FLIP_DURATION, ease: "easeInOut" }
                          : { duration: 0 }
                      }
                    >
                      <div
                        className="absolute inset-0"
                        style={{
                          backfaceVisibility: "hidden",
                          WebkitBackfaceVisibility: "hidden",
                          transform: "rotateY(0deg)",
                        }}
                      >
                        {leaf.front}
                      </div>
                      <div
                        className="absolute inset-0"
                        style={{
                          backfaceVisibility: "hidden",
                          WebkitBackfaceVisibility: "hidden",
                          transform: "rotateY(180deg)",
                        }}
                      >
                        {leaf.back}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-10 z-20 pointer-events-none bg-gradient-to-r from-black/25 via-black/5 to-black/25" />
              <div className="absolute inset-y-0 right-0 w-3 z-20 pointer-events-none bg-gradient-to-l from-black/20 to-transparent" />

              <button
                onClick={() => turn(1)}
                aria-label="Turn page forward"
                className="absolute inset-y-0 left-1/2 right-0 z-10 cursor-pointer group"
              >
                <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </button>
              <button
                onClick={() => turn(-1)}
                aria-label="Turn page backward"
                className="absolute inset-y-0 right-1/2 left-0 z-10 cursor-pointer group"
              >
                <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </button>
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => turn(-1)}
                disabled={flipCount === 0}
                aria-label="Previous page"
                className="p-2.5 rounded-full border border-white/15 bg-white/5 text-white/80 hover:border-nothing-red hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: N + 1 }, (_, d) => (
                  <span
                    key={d}
                    className={`h-1.5 rounded-full transition-all ${
                      d === flipCount ? "w-6 bg-nothing-red" : "w-1.5 bg-white/25"
                    }`}
                  />
                ))}
                <span className="ml-2 font-mono text-[10px] tracking-[0.2em] text-white/50">
                  {flipCount === 0
                    ? "COVER"
                    : flipCount === N
                    ? "BACK"
                    : `PAGE ${String(flipCount * 2 - 1).padStart(2, "0")}/${String(flipCount * 2).padStart(2, "0")}`}
                </span>
              </div>

              <button
                onClick={() => turn(1)}
                disabled={flipCount === N}
                aria-label="Next page"
                className="p-2.5 rounded-full border border-white/15 bg-white/5 text-white/80 hover:border-nothing-red hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <p className="text-center mt-3 font-mono text-[9px] tracking-[0.3em] text-white/35">
              CLICK PAGE OR USE ← → TO TURN
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
